import { useState, useRef, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import "./index.css";

// ─── Water Shader Material ───────────────────────────────────────────────
const waterVertexShader = `
  uniform float uTime;
  varying vec2 vUv;
  varying vec3 vPosition;
  varying vec3 vNormal;

  void main() {
    vUv = uv;
    vPosition = position;
    vNormal = normal;
    vec3 pos = position;
    pos.y += sin(pos.x * 3.0 + uTime * 2.0) * 0.05;
    pos.y += sin(pos.z * 2.0 + uTime * 1.5) * 0.03;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
  }
`;

const waterFragmentShader = `
  uniform float uTime;
  varying vec2 vUv;
  varying vec3 vPosition;
  varying vec3 vNormal;

  void main() {
    vec3 deepBlue = vec3(0.05, 0.15, 0.45);
    vec3 lightBlue = vec3(0.2, 0.55, 0.85);
    vec3 shimmer = vec3(0.7, 0.85, 1.0);

    float wave = sin(vUv.x * 20.0 + uTime * 3.0) * 0.5 + 0.5;
    float wave2 = sin(vUv.y * 15.0 + uTime * 2.0) * 0.5 + 0.5;
    float combined = wave * 0.5 + wave2 * 0.5;

    vec3 color = mix(deepBlue, lightBlue, combined);
    float sparkle = pow(sin(vUv.x * 50.0 + uTime * 5.0) * sin(vUv.y * 50.0 + uTime * 3.0), 8.0);
    color = mix(color, shimmer, sparkle * 0.5);

    gl_FragColor = vec4(color, 0.85);
  }
`;

// ─── Limestone Shader Material ────────────────────────────────────────────
const limestoneVertexShader = `
  varying vec2 vUv;
  varying vec3 vNormal;
  varying vec3 vWorldPos;

  void main() {
    vUv = uv;
    vNormal = normalize(normalMatrix * normal);
    vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const limestoneFragmentShader = `
  varying vec2 vUv;
  varying vec3 vNormal;
  varying vec3 vWorldPos;

  // Simple noise
  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }

  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float a = hash(i);
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }

  void main() {
    vec3 baseColor = vec3(0.85, 0.78, 0.65);
    vec3 weathered = vec3(0.72, 0.65, 0.52);
    vec3 light = vec3(0.92, 0.88, 0.80);

    float n = noise(vUv * 8.0);
    float n2 = noise(vUv * 25.0);
    float n3 = noise(vWorldPos.xz * 3.0);

    vec3 color = mix(baseColor, weathered, n * 0.6);
    color = mix(color, light, n2 * 0.2);
    color = mix(color, weathered * 0.8, n3 * 0.15);

    // Simple directional lighting
    vec3 lightDir = normalize(vec3(1.0, 1.5, 0.5));
    float diff = max(dot(vNormal, lightDir), 0.0);
    float ambient = 0.4;
    color *= (ambient + diff * 0.6);

    gl_FragColor = vec4(color, 1.0);
  }
`;

// ─── Generate Archimedes' Screw geometry ──────────────────────────────────
function createScrewGeometry(
	screwLength: number,
	outerRadius: number,
	innerRadius: number,
	helixTurns: number,
	segments: number
): THREE.BufferGeometry {
	const positions: number[] = [];
	const normals: number[] = [];
	const uvs: number[] = [];
	const indices: number[] = [];

	// Create the helical blade
	const bladeSegments = segments;
	const radialSegments = 12;

	for (let i = 0; i <= bladeSegments; i++) {
		const t = i / bladeSegments;
		const z = t * screwLength - screwLength / 2; // centered at origin
		const baseAngle = t * helixTurns * Math.PI * 2;

		for (let j = 0; j <= radialSegments; j++) {
			const r = innerRadius + (j / radialSegments) * (outerRadius - innerRadius);
			const angle = baseAngle;

			const x = r * Math.cos(angle);
			const y = r * Math.sin(angle);

			positions.push(x, y, z);

			// Approximate normal
			const nx = Math.cos(angle) * 0.3;
			const ny = Math.sin(angle) * 0.3;
			const nz = 1.0;
			const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
			normals.push(nx / len, ny / len, nz / len);

			uvs.push(t, j / radialSegments);

			if (i < bladeSegments && j < radialSegments) {
				const a = i * (radialSegments + 1) + j;
				const b = a + 1;
				const c = a + radialSegments + 1;
				const d = c + 1;
				indices.push(a, c, b);
				indices.push(b, c, d);
			}
		}
	}

	const geometry = new THREE.BufferGeometry();
	geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
	geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
	geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
	geometry.setIndex(indices);
	geometry.computeVertexNormals();

	return geometry;
}

// ─── Archimedes' Screw Component ─────────────────────────────────────────
function ArchimedesScrew({
	angle,
	rotationSpeed,
}: {
	angle: number;
	rotationSpeed: number;
}) {
	const screwRef = useRef<THREE.Group>(null);
	const bladeRef = useRef<THREE.Group>(null);
	const waterParticlesRef = useRef<THREE.InstancedMesh>(null);
	const waterMaterialRef = useRef<THREE.ShaderMaterial>(null);
	const lowerWaterRef = useRef<THREE.ShaderMaterial>(null);
	const upperWaterRef = useRef<THREE.ShaderMaterial>(null);

	const screwLength = 6;
	const outerRadius = 0.8;
	const innerRadius = 0.15;
	const helixTurns = 4;
	const particleCount = 200;

	// Create screw blade geometry
	const bladeGeometry = useMemo(
		() => createScrewGeometry(screwLength, outerRadius, innerRadius, helixTurns, 200),
		[]
	);

	// Create second blade (offset by PI)
	const bladeGeometry2 = useMemo(() => {
		const geo = createScrewGeometry(screwLength, outerRadius, innerRadius, helixTurns, 200);
		const pos = geo.attributes.position;
		const newPositions = new Float32Array(pos.array.length);

		for (let i = 0; i < pos.count; i++) {
			const x = pos.getX(i);
			const y = pos.getY(i);
			const z = pos.getZ(i);
			const r = Math.sqrt(x * x + y * y);
			const a = Math.atan2(y, x) + Math.PI;
			newPositions[i * 3] = r * Math.cos(a);
			newPositions[i * 3 + 1] = r * Math.sin(a);
			newPositions[i * 3 + 2] = z;
		}
		geo.setAttribute("position", new THREE.Float32BufferAttribute(newPositions, 3));
		geo.computeVertexNormals();
		return geo;
	}, []);

	// Water particle positions and velocities
	const particleData = useMemo(() => {
		const data = [];
		for (let i = 0; i < particleCount; i++) {
			data.push({
				phase: Math.random() * Math.PI * 2,
				speed: 0.5 + Math.random() * 0.5,
				radialOffset: 0.2 + Math.random() * 0.5,
				t: Math.random(),
			});
		}
		return data;
	}, []);

	const dummy = useMemo(() => new THREE.Object3D(), []);

	useFrame((state) => {
		const time = state.clock.elapsedTime;

		// Rotate the screw blades around Y (the cylinder's long axis)
		if (bladeRef.current) {
			bladeRef.current.rotation.y += rotationSpeed * 0.02;
		}

		// Animate water particles along the screw (Y is the long axis)
		// Particles are NOT children of the rotating blade group, so we
		// compute their positions in the screw's local space directly.
		if (waterParticlesRef.current) {
			const bladeRotation = bladeRef.current?.rotation.y || 0;
			for (let i = 0; i < particleCount; i++) {
				const p = particleData[i];
				p.t += rotationSpeed * 0.003 * p.speed;
				// Smoothly wrap around instead of a hard jump
				while (p.t > 1) p.t -= 1;
				while (p.t < 0) p.t += 1;

				const py = p.t * screwLength - screwLength / 2;
				const helixAngle =
					p.t * helixTurns * Math.PI * 2 + p.phase + bladeRotation;
				// Clamp radius so particles always stay inside the outer cylinder
				const r = Math.min(p.radialOffset, outerRadius - 0.05);

				const px = r * Math.cos(helixAngle);
				const pz = r * Math.sin(helixAngle);

				dummy.position.set(px, py, pz);
				const scale = 0.04 + Math.sin(time * 3 + i) * 0.01;
				dummy.scale.set(scale, scale, scale);
				dummy.updateMatrix();
				waterParticlesRef.current.setMatrixAt(i, dummy.matrix);
			}
			waterParticlesRef.current.instanceMatrix.needsUpdate = true;
		}

		// Animate water shaders
		if (waterMaterialRef.current) {
			waterMaterialRef.current.uniforms.uTime.value = time;
		}
		if (lowerWaterRef.current) {
			lowerWaterRef.current.uniforms.uTime.value = time;
		}
		if (upperWaterRef.current) {
			upperWaterRef.current.uniforms.uTime.value = time;
		}
	});

	const angleRad = (angle * Math.PI) / 180;

	// Marble material for the screw blades
	const marbleMaterial = useMemo(
		() =>
			new THREE.MeshStandardMaterial({
				color: new THREE.Color(0.9, 0.87, 0.82),
				roughness: 0.4,
				metalness: 0.05,
				side: THREE.DoubleSide,
			}),
		[]
	);

	return (
		<group ref={screwRef} rotation={[0, 0, angleRad]} position={[0, 1.5, 0]}>
			{/* Outer cylinder (transparent to see inside) */}
			<mesh position={[0, 0, 0]}>
				<cylinderGeometry args={[outerRadius + 0.05, outerRadius + 0.05, screwLength, 32, 1, true]} />
				<meshStandardMaterial
					color="#c4b89a"
					roughness={0.7}
					metalness={0.05}
					transparent
					opacity={0.3}
					side={THREE.DoubleSide}
				/>
			</mesh>

			{/* Wooden support rings */}
			{[0, 1, 2, 3, 4, 5, 6].map((i) => (
				<mesh key={i} position={[0, -screwLength / 2 + i * 1, 0]}>
					<torusGeometry args={[outerRadius + 0.08, 0.04, 8, 32]} />
					<meshStandardMaterial color="#8B7355" roughness={0.8} metalness={0.1} />
				</mesh>
			))}

			{/* Inner shaft */}
			<mesh>
				<cylinderGeometry args={[innerRadius, innerRadius, screwLength + 0.4, 16]} />
				<shaderMaterial
					vertexShader={limestoneVertexShader}
					fragmentShader={limestoneFragmentShader}
				/>
			</mesh>

			{/* Rotating blade group */}
			<group ref={bladeRef}>
				{/* Helical blade 1 */}
				<mesh
					geometry={bladeGeometry}
					material={marbleMaterial}
					rotation={[Math.PI / 2, 0, 0]}
				/>
				{/* Helical blade 2 */}
				<mesh
					geometry={bladeGeometry2}
					material={marbleMaterial}
					rotation={[Math.PI / 2, 0, 0]}
				/>
			</group>

			{/* Water particles — NOT a child of the rotating blade group
			    so they don't get double-rotated */}
			<instancedMesh
				ref={waterParticlesRef}
				args={[undefined, undefined, particleCount]}
			>
				<sphereGeometry args={[1, 8, 8]} />
				<meshStandardMaterial
					color="#3399ee"
					roughness={0.1}
					metalness={0.3}
					transparent
					opacity={0.8}
				/>
			</instancedMesh>

			{/* Decorative end caps - limestone */}
			<mesh position={[0, screwLength / 2 + 0.1, 0]}>
				<cylinderGeometry args={[outerRadius + 0.15, outerRadius + 0.1, 0.2, 32]} />
				<shaderMaterial
					vertexShader={limestoneVertexShader}
					fragmentShader={limestoneFragmentShader}
				/>
			</mesh>
			<mesh position={[0, -screwLength / 2 - 0.1, 0]}>
				<cylinderGeometry args={[outerRadius + 0.1, outerRadius + 0.15, 0.2, 32]} />
				<shaderMaterial
					vertexShader={limestoneVertexShader}
					fragmentShader={limestoneFragmentShader}
				/>
			</mesh>
		</group>
	);
}

// ─── Water Basin (Lower) ─────────────────────────────────────────────────
function WaterBasin({ position, size }: { position: [number, number, number]; size: [number, number, number] }) {
	const waterRef = useRef<THREE.ShaderMaterial>(null);

	useFrame((state) => {
		if (waterRef.current) {
			waterRef.current.uniforms.uTime.value = state.clock.elapsedTime;
		}
	});

	return (
		<group position={position}>
			{/* Basin walls - limestone */}
			<mesh position={[0, size[1] / 2, 0]}>
				<boxGeometry args={[size[0] + 0.3, size[1], size[2] + 0.3]} />
				<shaderMaterial
					vertexShader={limestoneVertexShader}
					fragmentShader={limestoneFragmentShader}
				/>
			</mesh>
			{/* Water surface */}
			<mesh position={[0, size[1] - 0.05, 0]} rotation={[-Math.PI / 2, 0, 0]}>
				<planeGeometry args={[size[0], size[2], 32, 32]} />
				<shaderMaterial
					ref={waterRef}
					vertexShader={waterVertexShader}
					fragmentShader={waterFragmentShader}
					uniforms={{ uTime: { value: 0 } }}
					transparent
					side={THREE.DoubleSide}
				/>
			</mesh>
		</group>
	);
}

// ─── Water Flow Stream (upper discharge) ─────────────────────────────────
function WaterStream({ angle, rotationSpeed }: { angle: number; rotationSpeed: number }) {
	const particlesRef = useRef<THREE.InstancedMesh>(null);
	const count = 60;
	const dummy = useMemo(() => new THREE.Object3D(), []);

	const particles = useMemo(() => {
		return Array.from({ length: count }, () => ({
			t: Math.random(),
			xOff: (Math.random() - 0.5) * 0.3,
			zOff: (Math.random() - 0.5) * 0.3,
			speed: 0.5 + Math.random() * 1.0,
		}));
	}, []);

	useFrame(() => {
		if (!particlesRef.current || rotationSpeed < 0.05) return;

		const angleRad = (angle * Math.PI) / 180;
		// Compute discharge point at the top of the screw
		const screwLength = 6;
		const topY = 1.5 + Math.cos(angleRad) * screwLength / 2;
		const topX = Math.sin(angleRad) * screwLength / 2;

		// Target is the upper basin position
		const basinX = 2.5;
		const basinY = 1.5 + 0.6; // basin position.y + basin height

		for (let i = 0; i < count; i++) {
			const p = particles[i];
			p.t += 0.01 * p.speed * rotationSpeed;
			while (p.t > 1) p.t -= 1;

			// Parabolic arc from top of screw to upper basin
			// Use a proper arc that peaks above both start and end points
			const startY = topY;
			const endY = basinY;
			const peakHeight = Math.max(startY, endY) + 0.5;
			const x = topX + p.t * (basinX - topX) + p.xOff * p.t;
			// Parabola that goes through (0, startY), peaks around t=0.3, and ends at (1, endY)
			const baseY = startY + p.t * (endY - startY);
			const arcOffset = 4 * (peakHeight - (startY + endY) / 2) * p.t * (1 - p.t);
			const y = baseY + arcOffset;
			const z = p.zOff * p.t;

			const scale = 0.05 * (1 - p.t * 0.5);
			dummy.position.set(x, y, z);
			dummy.scale.set(scale, scale, scale);
			dummy.updateMatrix();
			particlesRef.current.setMatrixAt(i, dummy.matrix);
		}
		particlesRef.current.instanceMatrix.needsUpdate = true;
	});

	if (rotationSpeed < 0.05) return null;

	return (
		<instancedMesh ref={particlesRef} args={[undefined, undefined, count]}>
			<sphereGeometry args={[1, 6, 6]} />
			<meshStandardMaterial
				color="#4dabf7"
				roughness={0.1}
				metalness={0.2}
				transparent
				opacity={0.7}
			/>
		</instancedMesh>
	);
}

// ─── Ground / Terrain ────────────────────────────────────────────────────
function Ground() {
	return (
		<mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -1.0, 0]} receiveShadow>
			<planeGeometry args={[40, 40, 64, 64]} />
			<shaderMaterial
				vertexShader={`
          varying vec2 vUv;
          varying vec3 vWorldPos;
          void main() {
            vUv = uv;
            vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `}
				fragmentShader={`
          varying vec2 vUv;
          varying vec3 vWorldPos;
          float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1,311.7)))*43758.5453); }
          float noise(vec2 p) {
            vec2 i = floor(p); vec2 f = fract(p);
            f = f*f*(3.0-2.0*f);
            return mix(mix(hash(i), hash(i+vec2(1,0)), f.x),
                       mix(hash(i+vec2(0,1)), hash(i+vec2(1,1)), f.x), f.y);
          }
          void main() {
            vec3 sand = vec3(0.82, 0.72, 0.55);
            vec3 dirt = vec3(0.65, 0.55, 0.40);
            float n = noise(vUv * 20.0);
            float n2 = noise(vUv * 60.0);
            vec3 color = mix(sand, dirt, n * 0.5 + n2 * 0.2);
            float dist = length(vWorldPos.xz);
            color = mix(color, color * 0.7, smoothstep(8.0, 20.0, dist));
            gl_FragColor = vec4(color, 1.0);
          }
        `}
			/>
		</mesh>
	);
}

// ─── Stone Pillars ───────────────────────────────────────────────────────
function StonePillar({ position, height }: { position: [number, number, number]; height: number }) {
	return (
		<group position={position}>
			{/* Main pillar */}
			<mesh position={[0, height / 2, 0]}>
				<cylinderGeometry args={[0.2, 0.25, height, 8]} />
				<shaderMaterial
					vertexShader={limestoneVertexShader}
					fragmentShader={limestoneFragmentShader}
				/>
			</mesh>
			{/* Capital */}
			<mesh position={[0, height, 0]}>
				<cylinderGeometry args={[0.35, 0.2, 0.2, 8]} />
				<shaderMaterial
					vertexShader={limestoneVertexShader}
					fragmentShader={limestoneFragmentShader}
				/>
			</mesh>
			{/* Base */}
			<mesh position={[0, 0.1, 0]}>
				<cylinderGeometry args={[0.25, 0.35, 0.2, 8]} />
				<shaderMaterial
					vertexShader={limestoneVertexShader}
					fragmentShader={limestoneFragmentShader}
				/>
			</mesh>
		</group>
	);
}

// ─── Support Structure ───────────────────────────────────────────────────
function SupportStructure({ angle }: { angle: number }) {
	const angleRad = (angle * Math.PI) / 180;
	const screwLength = 6;
	const bottomY = 1.5 - Math.cos(angleRad) * screwLength / 2;
	const topY = 1.5 + Math.cos(angleRad) * screwLength / 2;
	const bottomX = -Math.sin(angleRad) * screwLength / 2;
	const topX = Math.sin(angleRad) * screwLength / 2;

	return (
		<group>
			{/* Lower support */}
			<StonePillar position={[bottomX - 0.5, -1.0, -0.8]} height={bottomY + 0.5} />
			<StonePillar position={[bottomX - 0.5, -1.0, 0.8]} height={bottomY + 0.5} />
			{/* Upper support */}
			<StonePillar position={[topX + 0.5, -1.0, -0.8]} height={topY + 0.5} />
			<StonePillar position={[topX + 0.5, -1.0, 0.8]} height={topY + 0.5} />
		</group>
	);
}

// ─── Decorative columns in background ────────────────────────────────────
function BackgroundColumns() {
	const columns: { position: [number, number, number]; height: number }[] = [
		{ position: [-8, -1.0, -6], height: 3.2 },
		{ position: [-6, -1.0, -7], height: 4.1 },
		{ position: [-4, -1.0, -8], height: 3.7 },
		{ position: [4, -1.0, -8], height: 4.3 },
		{ position: [6, -1.0, -7], height: 3.5 },
		{ position: [8, -1.0, -6], height: 3.9 },
	];

	return (
		<>
			{columns.map((col, i) => (
				<StonePillar key={i} position={col.position} height={col.height} />
			))}
		</>
	);
}

// ─── Scene Lighting ──────────────────────────────────────────────────────
function SceneLighting() {
	return (
		<>
			<ambientLight intensity={0.4} color="#ffeedd" />
			<directionalLight
				position={[10, 15, 5]}
				intensity={1.8}
				color="#fff5e6"
				castShadow
				shadow-mapSize-width={2048}
				shadow-mapSize-height={2048}
			/>
			<directionalLight position={[-5, 8, -3]} intensity={0.4} color="#aaccff" />
			<pointLight position={[0, 5, 3]} intensity={0.6} color="#ffddaa" distance={15} />
		</>
	);
}

// ─── Sky gradient ────────────────────────────────────────────────────────
function Sky() {
	return (
		<mesh position={[0, 10, -20]} scale={[60, 30, 1]}>
			<planeGeometry args={[1, 1]} />
			<shaderMaterial
				vertexShader={`
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `}
				fragmentShader={`
          varying vec2 vUv;
          void main() {
            vec3 top = vec3(0.25, 0.45, 0.80);
            vec3 mid = vec3(0.55, 0.70, 0.90);
            vec3 bottom = vec3(0.95, 0.85, 0.70);
            vec3 color;
            if (vUv.y > 0.5) {
              color = mix(mid, top, (vUv.y - 0.5) * 2.0);
            } else {
              color = mix(bottom, mid, vUv.y * 2.0);
            }
            gl_FragColor = vec4(color, 1.0);
          }
        `}
				side={THREE.DoubleSide}
			/>
		</mesh>
	);
}

// ─── Main Scene ──────────────────────────────────────────────────────────
function Scene({ angle, rotationSpeed }: { angle: number; rotationSpeed: number }) {
	return (
		<>
			<SceneLighting />
			<Sky />
			<fog attach="fog" args={["#d4c4a8", 15, 40]} />

			<ArchimedesScrew angle={angle} rotationSpeed={rotationSpeed} />
			<SupportStructure angle={angle} />

			{/* Lower water basin */}
			<WaterBasin position={[-2, -1.0, 0]} size={[3, 0.8, 2.5]} />

			{/* Upper receiving basin */}
			<WaterBasin position={[2.5, 1.5, 0]} size={[2.5, 0.6, 2]} />

			{/* Water stream from screw to upper basin */}
			<WaterStream angle={angle} rotationSpeed={rotationSpeed} />

			<Ground />
			<BackgroundColumns />

			<OrbitControls
				enablePan={true}
				enableZoom={true}
				enableRotate={true}
				minDistance={3}
				maxDistance={25}
				target={[0, 1.5, 0]}
			/>
		</>
	);
}

// ─── UI Slider Component ─────────────────────────────────────────────────
function Slider({
	label,
	value,
	min,
	max,
	step,
	onChange,
	unit,
}: {
	label: string;
	value: number;
	min: number;
	max: number;
	step: number;
	onChange: (v: number) => void;
	unit: string;
}) {
	return (
		<div className="flex flex-col gap-1.5">
			<div className="flex justify-between items-center">
				<span className="text-xs font-medium tracking-wider uppercase text-[#5c4a2e]">
					{label}
				</span>
				<span className="text-xs font-mono text-[#8a7450] bg-[#f5edd8] px-2 py-0.5 rounded">
					{value.toFixed(1)}{unit}
				</span>
			</div>
			<input
				type="range"
				min={min}
				max={max}
				step={step}
				value={value}
				onChange={(e) => onChange(parseFloat(e.target.value))}
				className="slider-ancient w-full h-2 rounded-full appearance-none cursor-pointer bg-[#d4c4a8]"
			/>
		</div>
	);
}

// ─── App ─────────────────────────────────────────────────────────────────
export function App() {
	const [angle, setAngle] = useState(30);
	const [rotationSpeed, setRotationSpeed] = useState(1.0);

	return (
		<div className="w-screen h-screen overflow-hidden relative" style={{ background: "#c9b896" }}>
			{/* 3D Canvas */}
			<Canvas
				camera={{ position: [6, 4, 8], fov: 50 }}
				className="absolute inset-0"
				gl={{ antialias: true, alpha: false }}
				onCreated={({ gl }) => {
					gl.setClearColor("#c9b896");
					gl.toneMapping = THREE.ACESFilmicToneMapping;
					gl.toneMappingExposure = 1.2;
				}}
			>
				<Scene angle={angle} rotationSpeed={rotationSpeed} />
			</Canvas>

			{/* Title overlay */}
			<div className="absolute top-6 left-1/2 -translate-x-1/2 text-center pointer-events-none select-none">
				<h1
					className="text-3xl md:text-4xl font-bold tracking-widest uppercase"
					style={{
						color: "#3d2e14",
						textShadow: "0 2px 8px rgba(201, 184, 150, 0.8)",
						fontFamily: "Georgia, 'Times New Roman', serif",
					}}
				>
					Archimedes' Screw
				</h1>
				<p
					className="text-sm mt-1 tracking-wide"
					style={{
						color: "#6b5a3e",
						fontFamily: "Georgia, 'Times New Roman', serif",
					}}
				>
					An Ancient Water-Lifting Device ~ c. 250 BC
				</p>
			</div>

			{/* Controls panel */}
			<div
				className="absolute bottom-6 left-1/2 -translate-x-1/2 w-[90%] max-w-md rounded-xl p-5 backdrop-blur-md border shadow-2xl"
				style={{
					background: "rgba(245, 237, 216, 0.92)",
					borderColor: "rgba(180, 160, 120, 0.5)",
					boxShadow: "0 8px 32px rgba(60, 46, 20, 0.2), inset 0 1px 0 rgba(255,255,255,0.3)",
				}}
			>
				{/* Decorative header */}
				<div className="flex items-center gap-3 mb-4 pb-3" style={{ borderBottom: "1px solid rgba(180,160,120,0.3)" }}>
					<div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "#c9b896" }}>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5c4a2e" strokeWidth="2">
							<circle cx="12" cy="12" r="10" />
							<path d="M12 6v12M6 12h12" />
						</svg>
					</div>
					<div>
						<h2
							className="text-sm font-bold tracking-wider uppercase"
							style={{ color: "#3d2e14", fontFamily: "Georgia, serif" }}
						>
							Controls
						</h2>
						<p className="text-[10px] tracking-wide" style={{ color: "#8a7450" }}>
							Adjust the screw parameters
						</p>
					</div>
				</div>

				<div className="flex flex-col gap-4">
					<Slider
						label="Screw Angle"
						value={angle}
						min={10}
						max={60}
						step={0.5}
						onChange={setAngle}
						unit="°"
					/>
					<Slider
						label="Rotation Speed"
						value={rotationSpeed}
						min={0}
						max={5}
						step={0.1}
						onChange={setRotationSpeed}
						unit="x"
					/>
				</div>

				{/* Info footer */}
				<div
					className="mt-4 pt-3 text-[10px] text-center"
					style={{
						borderTop: "1px solid rgba(180,160,120,0.3)",
						color: "#8a7450",
						fontFamily: "Georgia, serif",
					}}
				>
					Drag to orbit &bull; Scroll to zoom &bull; Right-click to pan
				</div>
			</div>
		</div>
	);
}

export default App;
